import { useEffect, useRef, useState } from "react";

import type { CalculationIn, CalculationParamsOut, OptionsOut } from "@/api";
import { MoneyField } from "@/components/fields/MoneyField";
import { SelectField } from "@/components/fields/SelectField";

import styles from "./CalculationScreen.module.css";

/** The margin the engine accepts (`params.validate_params`); outside it the server answers 422 — the field says it first. */
const MARGIN_MIN = 5;
const MARGIN_MAX = 95;
const MARGIN_RANGE = "Marge zwischen 5 und 95 %.";

/**
 * Material · Tragstab · Maschenweite · Rutschhemmung · Marge (D-5). Options
 * come from the calculation itself (`CalculationOut.options`), so the browser
 * never carries a list the engine does not.
 *
 * A select recalculates at once — one click, one intention. The margin is
 * typed and commits on blur or Enter (design §9): every keystroke of „27,5“
 * would otherwise be a run and a stored row. Remounted per calculation by
 * the screen (`key={calc.id}`), so the draft starts from the stored figure.
 */
export function ParameterBar({
  params,
  options,
  disabled,
  runFailed,
  onChange,
}: {
  params: CalculationParamsOut;
  options: OptionsOut;
  disabled: boolean;
  /**
   * Whether the calculation's last run came back a failure — any parameter,
   * not the margin alone (`CalculationScreen`'s one shared `run`). Read only
   * to reset {@link lastCommittedRef} below (S-5 re-review): a run that
   * failed committed nothing, so the guard must not go on believing it did.
   */
  runFailed: boolean;
  onChange: (patch: Partial<CalculationIn>) => void;
}) {
  const [marginDraft, setMarginDraft] = useState(params.marginPct);
  const [marginError, setMarginError] = useState<string | null>(null);
  /**
   * The margin text last *successfully* sent onward, or `null` where the
   * last attempt failed and nothing has been resent since. Enter and the
   * blur that follows it both call `commitMargin` — a disabled input blurs
   * itself, and `onChange` above disables the bar the instant Enter's commit
   * starts the rerun — so without this the same figure went out twice: once
   * as the 201 that announced it, once more as the 200 "Unverändert" that
   * overwrote that announcement (S-5). Starts at the stored figure, the same
   * baseline `commitMargin`'s own guard already compared against.
   *
   * Set optimistically in `commitMargin`, *before* the run is known to
   * succeed — updating it only in a success callback would need `onChange`
   * to thread one through `CalculationScreen`'s `rerun`, for every caller of
   * this bar, not only the margin. Correctness instead comes from the effect
   * below, which un-sets it the moment that optimism turns out wrong: a 422,
   * a 500, a network failure. Without that reset, retrying the exact same
   * figure after a failure — the natural next thing a person does — hit this
   * guard and did nothing at all, leaving the stale error on screen with no
   * way to acknowledge it short of typing a different number and back
   * (S-5 re-review, Important).
   */
  const lastCommittedRef = useRef<string | null>(params.marginPct);

  useEffect(() => {
    if (runFailed) lastCommittedRef.current = null;
  }, [runFailed]);

  function commitMargin(): void {
    const value = Number(marginDraft);
    if (marginDraft === "" || Number.isNaN(value) || value < MARGIN_MIN || value > MARGIN_MAX) {
      setMarginError(MARGIN_RANGE);
      return;
    }
    setMarginError(null);
    if (
      (lastCommittedRef.current !== null && value === Number(lastCommittedRef.current)) ||
      value === Number(params.marginPct)
    ) {
      return;
    }
    lastCommittedRef.current = marginDraft;
    onChange({ marginPct: marginDraft });
  }

  return (
    <div className={styles.params} role="group" aria-label="Parameter der Kalkulation">
      <SelectField
        id="calc-material"
        label="Material"
        value={params.material}
        options={options.materials}
        disabled={disabled}
        onChange={(material) => onChange({ material })}
      />
      <SelectField
        id="calc-tragstab"
        label="Tragstab"
        value={params.tragstab}
        options={options.bearingBars}
        disabled={disabled}
        onChange={(tragstab) => onChange({ tragstab })}
      />
      <SelectField
        id="calc-masche"
        label="Maschenweite"
        value={params.maschenweite}
        options={options.meshes}
        disabled={disabled}
        onChange={(maschenweite) => onChange({ maschenweite })}
      />
      <SelectField
        id="calc-rh"
        label="Rutschhemmung"
        value={params.rutschhemmung}
        options={options.antiSlips}
        disabled={disabled}
        onChange={(rutschhemmung) => onChange({ rutschhemmung })}
      />
      {/* Blur and Enter bubble from the input; committing here keeps
          MoneyField itself free of a second way to finish. */}
      <div
        onBlur={commitMargin}
        onKeyDown={(event) => {
          if (event.key === "Enter") commitMargin();
        }}
      >
        <MoneyField
          id="calc-margin"
          label="Marge %"
          // The wire sends a dot ("27.5"); shown with a comma, exactly as
          // typed, the way the field's own edits already normalise it back
          // to a dot underneath (S-6). A straight substitution, never a
          // `Number()` — `PlanningScreen.tsx`'s CSV export keeps the same
          // rule for the same reason.
          value={marginDraft.replace(".", ",")}
          disabled={disabled}
          error={marginError}
          unitWord="Prozentsatz"
          hint="VK = EK ÷ (1 − Marge)"
          onChange={setMarginDraft}
        />
      </div>
      <p className={styles.reading}>
        <span className="label">Staffel</span>
        <span>je Position</span>
      </p>
    </div>
  );
}
